import {useConfig} from '@aboutyou/config';
import {useHistoryPageState} from '@aboutyou/history-page-state';
import {useTranslation} from '@aboutyou/i18n';
import {
  Button,
  COLORS,
  Checkbox,
  FONT_SIZES,
  FONT_WEIGHTS,
  GenericIconName,
  Icon,
  TextInput,
  Tooltip,
  UnstyledButton,
  desktopAndUp,
  tabletAndDown,
  useShowToast,
} from '@aboutyou/ui-components';
import {emailRegex} from '@aboutyou/utils';
import React, {useEffect, useMemo, useState} from 'react';
import {useForm} from 'react-hook-form';
import styled, {css} from 'styled-components';

export interface DOLFormData {
  orderNumber: string;
  trackingNumber: string;
  name: string;
  surname: string;
  email: string;
  street: string;
  houseNumber: string;
  postalCode: string;
  city: string;
  dateOfBirth: string;
  formConfirmed: boolean;
}

export const DeclarationOfLossForm = ({
  onSubmit,
  orderId,
  showCopyButtonOnOrderNumber,
  validateSecondaryGroup,
  isValidToSubmit,
}: {
  onSubmit: (data: DOLFormData) => void;
  orderId?: string;
  showCopyButtonOnOrderNumber: boolean;
  validateSecondaryGroup: VoidFunction;
  isValidToSubmit: boolean;
}) => {
  const {t} = useTranslation();
  const [formSavedData, setFormSavedData] = useHistoryPageState<
    undefined | DOLFormData
  >(undefined, {
    uniqueKey: 'DeclarationOfLossForm',
  });
  const {
    register,
    formState: {errors},
    handleSubmit,
    watch,
    setValue,
    getValues,
    trigger,
  } = useForm<DOLFormData>({
    mode: 'onBlur',
  });

  // on value filled out, it should save values in history storage
  useEffect(() => {
    const subscription = watch((value, _) => {
      // eslint-disable-next-line local-rules/set-state-in-use-effect
      setFormSavedData(value as DOLFormData);
    });
    return () => {
      if (subscription && subscription.unsubscribe) {
        subscription.unsubscribe();
      }
    };
  }, [setFormSavedData, watch]);

  // fill out orderId from url
  useEffect(() => {
    if (orderId) {
      // eslint-disable-next-line local-rules/set-state-in-use-effect
      setValue('orderNumber', orderId);
    }
  }, [orderId, setValue]);

  const areAllDataSaved = useMemo(() => {
    let returnValue = true;
    if (!formSavedData) {
      return false;
    }
    Object.entries(formSavedData).forEach(([_, value]) => {
      if (value === '') {
        returnValue = false;
      }
    });

    return returnValue;
  }, [formSavedData]);

  // fill out data if it exists from history
  useEffect(() => {
    if (formSavedData) {
      Object.entries(formSavedData).forEach(([key, value]) => {
        if (getValues(key as keyof DOLFormData) !== value) {
          // eslint-disable-next-line local-rules/set-state-in-use-effect
          setValue(key as keyof DOLFormData, value, {
            shouldDirty: true,
            shouldValidate: true,
          });
        }
      });
      if (areAllDataSaved) {
        trigger();
      }
    }
  }, [formSavedData, getValues, setValue, trigger, areAllDataSaved]);

  const submitHandler = (data: DOLFormData) => {
    if (isValidToSubmit) {
      onSubmit(data);
    }
  };

  const isInError = useMemo(() => Object.keys(errors).length > 0, [errors]);

  const CopyContentInputButton = () => {
    const [showTooltip, setShowTooltip] = useState(false);
    const {device} = useConfig();
    const showToast = useShowToast();

    const onCopyClick = () => {
      if (orderId && window.isSecureContext) {
        navigator.clipboard.writeText(orderId);
      }
      if (device === 'mobile') {
        showToast({
          type: 'SUCCESS',
          content: t('Order number copied to clipboard'),
        });
      } else {
        setShowTooltip(true);
        setTimeout(() => {
          setShowTooltip(false);
        }, 3000);
      }
    };

    return (
      <>
        <Tooltip
          show={showTooltip}
          label={t('Order number copied to clipboard')}
          placement="bottom-center"
          borderColor="Green"
          backgroundColor={'#F0FCF6'}
          theme="light"
        >
          <CopyContentWrapper
            onClick={onCopyClick}
            type="button"
            aria-label={t('Order number copied to clipboard')}
          >
            <Icon asset={GenericIconName.copy} size={'S'} />
          </CopyContentWrapper>
        </Tooltip>
      </>
    );
  };

  return (
    <>
      {isInError && (
        <WarningText>
          {t('Please fill in a valid value for all required fields.')}
        </WarningText>
      )}

      <StyledForm
        autoComplete="on"
        data-testid="sizeSubmission"
        onSubmit={handleSubmit(submitHandler)}
        method="POST"
      >
        <TextInput
          activeStyle={{
            backgroundColor: COLORS.White,
            borderColor: COLORS.Black,
          }}
          disabled={Boolean(orderId)}
          hideErrorIcon
          autoComplete="orderNumber"
          dataTestId="declarationOfLossOrderNumber"
          errorMessage={errors?.orderNumber?.message}
          label={t('Order number')}
          {...register('orderNumber', {
            required: t('Invalid number'),
          })}
          type="text"
          value={getValues('orderNumber')}
          {...(showCopyButtonOnOrderNumber
            ? {componentRight: {Component: CopyContentInputButton, width: 50}}
            : {})}
        />

        <TextInput
          activeStyle={{
            backgroundColor: COLORS.White,
            borderColor: COLORS.Black,
          }}
          hideErrorIcon
          autoComplete="trackingNumber"
          dataTestId="declarationOfLossTrackingNumber"
          errorMessage={errors.trackingNumber?.message}
          label={t('Tracking number*')}
          {...register('trackingNumber', {
            required: t('Invalid number'),
          })}
          type="text"
          value={getValues('trackingNumber')}
        />
        <TextInput
          activeStyle={{
            backgroundColor: COLORS.White,
            borderColor: COLORS.Black,
          }}
          hideErrorIcon
          autoComplete="name"
          dataTestId="declarationOfLossName"
          errorMessage={errors.name?.message}
          label={t('Name*')}
          {...register('name', {required: t('Invalid format')})}
          type="text"
          value={getValues('name')}
        />
        <TextInput
          activeStyle={{
            backgroundColor: COLORS.White,
            borderColor: COLORS.Black,
          }}
          hideErrorIcon
          autoComplete="Surname*"
          dataTestId="declarationOfLossSurname"
          errorMessage={errors.surname?.message}
          label={t('Surname*')}
          {...register('surname', {required: t('Invalid format')})}
          type="text"
          value={getValues('surname')}
        />
        <TextInput
          activeStyle={{
            backgroundColor: COLORS.White,
            borderColor: COLORS.Black,
          }}
          hideErrorIcon
          autoComplete="dateOfBirth"
          dataTestId="declarationOfLossDateOfBirth"
          errorMessage={errors.dateOfBirth?.message}
          {...register('dateOfBirth', {required: t('Invalid format')})}
          type="date"
          value={getValues('dateOfBirth')}
          label={t('Date of birth*')}
        />
        <TextInput
          activeStyle={{
            backgroundColor: COLORS.White,
            borderColor: COLORS.Black,
          }}
          hideErrorIcon
          autoComplete="email"
          dataTestId="declarationOfLossEmail"
          errorMessage={errors.email?.message}
          label={t('Email address*')}
          {...register('email', {
            required: true,
            pattern: {value: emailRegex, message: t('Invalid format')},
          })}
          type="email"
          value={getValues('email')}
        />
        <TextInput
          activeStyle={{
            backgroundColor: COLORS.White,
            borderColor: COLORS.Black,
          }}
          hideErrorIcon
          autoComplete="street"
          dataTestId="declarationOfLossStreet"
          errorMessage={errors.street?.message}
          label={t('Street*')}
          {...register('street', {required: t('Invalid format')})}
          type="text"
          value={getValues('street')}
        />
        <TextInput
          activeStyle={{
            backgroundColor: COLORS.White,
            borderColor: COLORS.Black,
          }}
          hideErrorIcon
          autoComplete="houseNumber"
          dataTestId="declarationOfLossHouseNumber"
          errorMessage={errors.houseNumber?.message}
          label={t('House number*')}
          {...register('houseNumber', {required: t('Invalid format')})}
          type="text"
          value={getValues('houseNumber')}
        />
        <TextInput
          activeStyle={{
            backgroundColor: COLORS.White,
            borderColor: COLORS.Black,
          }}
          hideErrorIcon
          autoComplete="postalCode"
          dataTestId="declarationOfLossPostalCode"
          errorMessage={errors.postalCode?.message}
          label={t('Postal code*')}
          {...register('postalCode', {required: t('Invalid format')})}
          type="text"
          value={getValues('postalCode')}
        />
        <TextInput
          activeStyle={{
            backgroundColor: COLORS.White,
            borderColor: COLORS.Black,
          }}
          hideErrorIcon
          autoComplete="city"
          dataTestId="declarationOfLossCity"
          errorMessage={errors.city?.message}
          label={t('City*')}
          {...register('city', {required: t('Invalid format')})}
          type="text"
          value={getValues('city')}
        />
        <div>
          {errors.formConfirmed?.message && (
            <WarningText>{errors.formConfirmed?.message}</WarningText>
          )}

          <StyledCheckbox
            {...register('formConfirmed', {
              required: t(
                'Please check the box below in order to submit the form.',
              ),
            })}
            label={t(
              'I hereby confirm that all information I have provided in this form is correct and complete.*',
            )}
          />
        </div>

        <Button htmlType="submit" onClick={validateSecondaryGroup}>
          {t('Submit a declaration of loss')}
        </Button>
      </StyledForm>
    </>
  );
};

const CopyContentWrapper = styled(UnstyledButton)`
  padding: 12px;
  height: calc(100% - 2px);
  width: 50px;
  border-radius: 4px;
  background-color: unset;
  border: unset;

  &:hover {
    background-color: ${COLORS.Grey4};
  }
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;

  ${desktopAndUp(css`
    gap: 24px;
  `)}
`;

const StyledCheckbox = styled(Checkbox)`
  color: ${COLORS.Black};
  margin-top: 12px;
`;

const WarningText = styled.p`
  font-size: ${FONT_SIZES.px14};
  font-weight: ${FONT_WEIGHTS.mediumBold};
  color: ${COLORS.Red};

  ${tabletAndDown(css`
    margin-bottom: 24px;
  `)}
`;
